import { useState, useMemo } from "react";
import {
  Megaphone,
  Pause,
  Play,
  Pencil,
  Trash2,
  AlertCircle,
  Plus,
  Search,
  Tag,
  ShoppingBag,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import {
  KPICard,
  EmptyState,
} from "@/components/admin/shared";
import { PromoFormModal } from "@/components/admin/marketing/PromoFormModal";
import {
  usePromotions,
  usePausePromotion,
  useResumePromotion,
  useDeletePromotion,
} from "@/hooks/admin/useMarketing";
import type {
  Promotion,
  PromoType,
  PromoStatus,
  PromotionListParams,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROMO_TYPE_LABELS: Record<PromoType, string> = {
  percentage: "Pourcentage",
  fixed_amount: "Montant fixe (FCFA)",
  free_shipping: "Livraison gratuite",
  buy_x_get_y: "Achetez X, obtenez Y",
};


const PROMO_STATUS_LABELS: Record<PromoStatus, string> = {
  active: "Active",
  scheduled: "Planifiée",
  expired: "Expirée",
  paused: "En pause",
};

const PROMO_STATUS_STYLES: Record<PromoStatus, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  expired: "bg-stone-100 text-stone-600 border-stone-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
};

const PROMO_TYPE_STYLES: Record<PromoType, string> = {
  percentage: "bg-purple-100 text-purple-800",
  fixed_amount: "bg-blue-100 text-blue-800",
  free_shipping: "bg-teal-100 text-teal-800",
  buy_x_get_y: "bg-orange-100 text-orange-800",
};

type StatusTab = "all" | PromoStatus;

const STATUS_TABS: ReadonlyArray<{ readonly key: StatusTab; readonly label: string }> = [
  { key: "all", label: "Toutes" },
  { key: "active", label: "Actives" },
  { key: "scheduled", label: "Planifiées" },
  { key: "expired", label: "Expirées" },
  { key: "paused", label: "En pause" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDiscount(promo: Promotion): string {
  if (promo.type === "percentage") {
    return `${promo.discount_value} %`;
  }
  if (promo.type === "fixed_amount") {
    return `${promo.discount_value.toLocaleString("fr-SN")} FCFA`;
  }
  if (promo.type === "free_shipping") return "Livraison offerte";
  return `${promo.discount_value}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Status tab bar
// ---------------------------------------------------------------------------

function StatusTabBar({
  activeTab,
  onChange,
  counts,
}: {
  readonly activeTab: StatusTab;
  readonly onChange: (tab: StatusTab) => void;
  readonly counts: Record<StatusTab, number>;
}) {
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`relative px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
          <span
            className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
              activeTab === tab.key
                ? "bg-amber-100 text-amber-700"
                : "bg-stone-100 text-stone-500"
            }`}
          >
            {counts[tab.key]}
          </span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

interface Filters {
  readonly type: PromoType | "";
  readonly search: string;
}

function PromoFilterBar({
  filters,
  onChange,
}: {
  readonly filters: Filters;
  readonly onChange: (f: Filters) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Rechercher une promotion..."
          className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-3 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
        />
      </div>
      <select
        value={filters.type}
        onChange={(e) =>
          onChange({ ...filters, type: e.target.value as PromoType | "" })
        }
        className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
      >
        <option value="">Tous les types</option>
        {(Object.keys(PROMO_TYPE_LABELS) as PromoType[]).map((t) => (
          <option key={t} value={t}>
            {PROMO_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage progress
// ---------------------------------------------------------------------------

function UsageProgress({
  usesCount,
  maxUses,
}: {
  readonly usesCount: number;
  readonly maxUses: number | null;
}) {
  if (maxUses == null) {
    return (
      <span className="tabular-nums text-stone-600">
        {usesCount.toLocaleString("fr-SN")}
      </span>
    );
  }
  const pct = Math.min((usesCount / maxUses) * 100, 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-stone-100">
        <div
          className={`h-full rounded-full transition-all ${
            pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-xs text-stone-600">
        {usesCount.toLocaleString("fr-SN")} / {maxUses.toLocaleString("fr-SN")}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Promotions() {
  useAdminPageTitle("Promotions");
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [filters, setFilters] = useState<Filters>({ type: "", search: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Promotion | null>(null);

  const queryParams: PromotionListParams = {
    type: filters.type || undefined,
    status: statusTab === "all" ? undefined : statusTab,
  };

  const { data, isLoading, isError } = usePromotions(queryParams);
  const pause = usePausePromotion();
  const resume = useResumePromotion();
  const remove = useDeletePromotion();

  const allPromotions = data?.promotions ?? [];

  // Client-side search filter
  const promotions = useMemo(() => {
    if (!filters.search.trim()) return allPromotions;
    const q = filters.search.toLowerCase();
    return allPromotions.filter((p) => p.name.toLowerCase().includes(q));
  }, [allPromotions, filters.search]);

  // Tab counts
  const tabCounts = useMemo((): Record<StatusTab, number> => {
    const all = data?.promotions ?? [];
    return {
      all: all.length,
      active: all.filter((p) => p.status === "active").length,
      scheduled: all.filter((p) => p.status === "scheduled").length,
      expired: all.filter((p) => p.status === "expired").length,
      paused: all.filter((p) => p.status === "paused").length,
    };
  }, [data?.promotions]);

  const totalDiscount = promotions.reduce(
    (acc, p) =>
      acc + p.uses_count * (p.type === "fixed_amount" ? p.discount_value : 0),
    0,
  );

  const handlePause = (id: string) => {
    pause.mutate(id, {
      onSuccess: () => toast.success("Promotion mise en pause."),
      onError: () => toast.error("Erreur lors de la mise en pause."),
    });
  };

  const handleResume = (id: string) => {
    resume.mutate(id, {
      onSuccess: () => toast.success("Promotion reactivee."),
      onError: () => toast.error("Erreur lors de la reactivation."),
    });
  };

  const handleDelete = (id: string, name: string, status: string) => {
    // Phase-2 bug #12: the delete button was previously gated on
    // `status === "expired" || "paused"`, so a freshly-created promotion
    // could not be removed from the UI — the QA tester had to call the
    // API directly. We now always surface delete, but show a louder
    // confirmation for active promos (they're likely in-flight on the
    // storefront).
    const warning =
      status === "active" || status === "scheduled"
        ? `Supprimer la promotion ACTIVE "${name}" ?\n\nLes clients ne pourront plus l'utiliser immédiatement.\nPour la désactiver temporairement, utilisez "Mettre en pause" à la place.`
        : `Supprimer la promotion "${name}" ?`;
    if (!confirm(warning)) return;
    remove.mutate(id, {
      onSuccess: () => toast.success("Promotion supprimee."),
      onError: () => toast.error("Erreur lors de la suppression."),
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Promotions
              </h1>
              <p className="text-sm text-stone-500">
                Gestion des offres et remises actives
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setEditTarget(null);
              setModalOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle promotion
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Actives" value={tabCounts.active} format="number" />
          <KPICard label="Planifiées" value={tabCounts.scheduled} format="number" />
          <KPICard label="Expirées" value={tabCounts.expired} format="number" />
          <KPICard
            label="Remises totales accordées"
            value={totalDiscount}
            format="currency"
          />
        </div>

        {/* Status tabs */}
        <StatusTabBar
          activeTab={statusTab}
          onChange={setStatusTab}
          counts={tabCounts}
        />

        {/* Filters */}
        <PromoFilterBar filters={filters} onChange={setFilters} />

        {/* Table */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les promotions.
          </div>
        )}

        {!isLoading && !isError && promotions.length === 0 && (
          <EmptyState
            icon={<Megaphone className="h-6 w-6" />}
            title="Aucune promotion"
            description={
              statusTab === "all"
                ? "Creez votre premiere promotion pour commencer."
                : `Aucune promotion ${PROMO_STATUS_LABELS[statusTab as PromoStatus]?.toLowerCase() ?? ""} pour le moment.`
            }
            action={{
              label: "Nouvelle promotion",
              onClick: () => {
                setEditTarget(null);
                setModalOpen(true);
              },
            }}
          />
        )}

        {!isLoading && !isError && promotions.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Remise
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Min. commande
                    </th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Utilisations
                    </th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Periode
                    </th>
                    <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Ciblage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Statut
                    </th>
                    <th className="w-28 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {promotions.map((p) => {
                    const hasProducts =
                      p.applicable_products && p.applicable_products.length > 0;
                    const hasCategories =
                      p.applicable_categories &&
                      p.applicable_categories.length > 0;

                    return (
                      <tr
                        key={p.id}
                        className="hover:bg-stone-50 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-stone-900">
                          {p.name}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PROMO_TYPE_STYLES[p.type]}`}
                          >
                            {PROMO_TYPE_LABELS[p.type]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-stone-800">
                          {formatDiscount(p)}
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                          {p.min_order_fcfa
                            ? `${p.min_order_fcfa.toLocaleString("fr-SN")} FCFA`
                            : "\u2014"}
                        </td>
                        <td className="hidden sm:table-cell px-4 py-3 text-right">
                          <UsageProgress
                            usesCount={p.uses_count}
                            maxUses={p.max_uses}
                          />
                        </td>
                        <td className="hidden lg:table-cell px-4 py-3 text-stone-500 text-xs">
                          {formatDate(p.starts_at)}
                          {p.ends_at && ` \u2192 ${formatDate(p.ends_at)}`}
                        </td>
                        <td className="hidden xl:table-cell px-4 py-3">
                          <div className="flex items-center gap-1">
                            {hasProducts && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-600"
                                title={`${p.applicable_products.length} produit(s) cible(s)`}
                              >
                                <ShoppingBag className="h-3 w-3" />
                                {p.applicable_products.length}
                              </span>
                            )}
                            {hasCategories && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-600"
                                title={`${p.applicable_categories.length} categorie(s) ciblee(s)`}
                              >
                                <Tag className="h-3 w-3" />
                                {p.applicable_categories.length}
                              </span>
                            )}
                            {!hasProducts && !hasCategories && (
                              <span className="text-xs text-stone-400">
                                Tous
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${PROMO_STATUS_STYLES[p.status]}`}
                          >
                            {PROMO_STATUS_LABELS[p.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              title="Modifier"
                              onClick={() => {
                                setEditTarget(p);
                                setModalOpen(true);
                              }}
                              className="rounded-md p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {p.status === "active" && (
                              <button
                                title="Mettre en pause"
                                onClick={() => handlePause(p.id)}
                                className="rounded-md p-1.5 text-stone-400 hover:bg-amber-50 hover:text-amber-700"
                              >
                                <Pause className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {p.status === "paused" && (
                              <button
                                title="Reactiver"
                                onClick={() => handleResume(p.id)}
                                className="rounded-md p-1.5 text-stone-400 hover:bg-emerald-50 hover:text-emerald-700"
                              >
                                <Play className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              title="Supprimer"
                              onClick={() => handleDelete(p.id, p.name, p.status)}
                              className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <PromoFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initial={editTarget ?? undefined}
      />
    </AdminLayout>
  );
}
